/** Componentes de interfaz pequeños y reutilizables. */
import { h, replace, s, type Child } from '../lib/dom';
import { errorMessage, t } from '../lib/i18n';
import { ApiError } from '../lib/api';
import { icon } from './icons';

// ---------------------------------------------------------------- Avatares generados (sin imágenes subidas)
const AVATAR_SHAPES: Record<string, string> = {
  keycap: 'M10 14h28v20H10z M14 18h20',
  caret: 'M24 10v28 M18 10h12 M18 38h12',
  spark: 'M24 8l4 12 12 4-12 4-4 12-4-12-12-4 12-4z',
  wave: 'M8 28c4-8 8-8 12 0s8 8 12 0 8-8 8-8',
  orbit: 'M24 14a10 10 0 1 0 0 20 10 10 0 0 0 0-20z M6 24h36',
  leaf: 'M12 36c0-16 10-24 26-24 0 16-8 26-26 24z M12 36l14-14',
  bolt: 'M26 6L12 28h11l-2 14 15-22H25z',
  crown: 'M8 18l8 8 8-12 8 12 8-8-4 18H12z',
};

export const AVATAR_IDS = Object.keys(AVATAR_SHAPES);

export function avatar(id: string, color: string, size = 40, label?: string): SVGSVGElement {
  return s(
    'svg',
    { viewBox: '0 0 48 48', width: size, height: size, class: 'avatar', role: label ? 'img' : undefined, 'aria-label': label, 'aria-hidden': label ? undefined : 'true' },
    s('rect', { width: 48, height: 48, rx: 14, fill: color }),
    s('path', { d: AVATAR_SHAPES[id] ?? AVATAR_SHAPES.keycap!, fill: 'none', stroke: 'rgba(10,10,15,0.85)', 'stroke-width': 3.2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }),
  );
}

// ---------------------------------------------------------------- Toasts
let toastRoot: HTMLElement | null = null;

export function toast(message: string, kind: 'info' | 'success' | 'error' = 'info', timeoutMs = 4000): void {
  toastRoot ??= document.getElementById('toasts');
  if (!toastRoot) return;
  const el = h('div', { class: `toast toast-${kind}`, role: kind === 'error' ? 'alert' : 'status' }, icon(kind === 'error' ? 'close' : kind === 'success' ? 'check' : 'sparkle', 18), h('span', null, message));
  toastRoot.appendChild(el);
  requestAnimationFrame(() => el.classList.add('show'));
  setTimeout(() => {
    el.classList.remove('show');
    setTimeout(() => el.remove(), 300);
  }, timeoutMs);
}

export function toastError(error: unknown): void {
  toast(describeError(error), 'error');
}

export function describeError(error: unknown): string {
  if (error instanceof ApiError) return errorMessage(error.code);
  return t('common.error');
}

// ---------------------------------------------------------------- Modal accesible
export function openModal(options: { title: string; body: Child; actions?: HTMLElement[]; onClose?: () => void; wide?: boolean }): { close: () => void; el: HTMLElement } {
  const previouslyFocused = document.activeElement as HTMLElement | null;
  const titleId = `modal-title-${Math.random().toString(36).slice(2)}`;
  const close = () => {
    overlay.classList.remove('show');
    document.removeEventListener('keydown', onKey);
    setTimeout(() => overlay.remove(), 200);
    previouslyFocused?.focus?.();
    options.onClose?.();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
    if (e.key === 'Tab') {
      const focusable = dialog.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) (e.preventDefault(), last.focus());
      else if (!e.shiftKey && document.activeElement === last) (e.preventDefault(), first.focus());
    }
  };
  const dialog = h(
    'div',
    { class: `modal${options.wide ? ' modal-wide' : ''}`, role: 'dialog', attrs: { 'aria-modal': 'true', 'aria-labelledby': titleId } },
    h('header', { class: 'modal-header' }, h('h2', { id: titleId }, options.title), h('button', { class: 'icon-btn', attrs: { 'aria-label': t('common.close') }, onClick: close }, icon('close'))),
    h('div', { class: 'modal-body' }, options.body),
    options.actions?.length ? h('footer', { class: 'modal-actions' }, options.actions) : null,
  );
  const overlay = h('div', { class: 'modal-overlay', onClick: (e: MouseEvent) => e.target === overlay && close() }, dialog);
  document.body.appendChild(overlay);
  document.addEventListener('keydown', onKey);
  requestAnimationFrame(() => {
    overlay.classList.add('show');
    (dialog.querySelector<HTMLElement>('input, textarea, select, .modal-actions button') ?? dialog.querySelector<HTMLElement>('button'))?.focus();
  });
  return { close, el: dialog };
}

export function confirmDialog(message: string, confirmLabel: string): Promise<boolean> {
  return new Promise((resolve) => {
    let decided = false;
    const yes = h('button', { class: 'btn btn-danger', onClick: () => ((decided = true), modal.close(), resolve(true)) }, confirmLabel);
    const no = h('button', { class: 'btn btn-ghost', onClick: () => modal.close() }, t('common.cancel'));
    const modal = openModal({ title: confirmLabel, body: h('p', null, message), actions: [no, yes], onClose: () => !decided && resolve(false) });
  });
}

// ---------------------------------------------------------------- Controles
export interface SegmentOption<T extends string | number> {
  value: T;
  label: string;
  title?: string;
}

/** Grupo de opciones exclusivas (radiogroup accesible). */
export function segmented<T extends string | number>(name: string, options: SegmentOption<T>[], value: T, onChange: (value: T) => void, label?: string): HTMLElement {
  const group = h('div', { class: 'segmented', role: 'radiogroup', attrs: { 'aria-label': label ?? name } });
  const buttons = options.map((option) =>
    h(
      'button',
      {
        type: 'button',
        class: 'segment',
        role: 'radio',
        title: option.title,
        attrs: { 'aria-checked': option.value === value ? 'true' : 'false', 'data-value': String(option.value) },
        onClick: () => {
          for (const b of buttons) b.setAttribute('aria-checked', b.dataset.value === String(option.value) ? 'true' : 'false');
          onChange(option.value);
        },
      },
      option.label,
    ),
  );
  group.append(...buttons);
  return radioKeys(group);
}

export function field(label: string, control: HTMLElement, hint?: string, id?: string): HTMLElement {
  const controlId = id || control.id || `f-${Math.random().toString(36).slice(2)}`;
  control.id = controlId;
  const hintId = hint ? `${controlId}-hint` : undefined;
  if (hintId) control.setAttribute('aria-describedby', hintId);
  return h('div', { class: 'field' }, h('label', { attrs: { for: controlId } }, label), control, hint ? h('p', { class: 'hint', id: hintId }, hint) : null);
}

export function toggle(label: string, checked: boolean, onChange: (checked: boolean) => void): HTMLElement {
  const input = h('input', { type: 'checkbox', checked, onChange: () => onChange(input.checked) });
  return h('label', { class: 'toggle' }, input, h('span', { class: 'toggle-track', attrs: { 'aria-hidden': 'true' } }), h('span', null, label));
}

export function select<T extends string>(options: { value: T; label: string }[], value: T, onChange: (value: T) => void): HTMLSelectElement {
  const el = h('select', { class: 'input', onChange: () => onChange(el.value as T) }, options.map((o) => h('option', { value: o.value, selected: o.value === value }, o.label)));
  return el;
}

/**
 * Teclado en grupos de radio (patrón ARIA): las flechas, Inicio y Fin mueven y
 * seleccionan, y solo la opción marcada entra en el orden de tabulación. Si al
 * elegir se vuelve a pintar el grupo, el foco pasa a la opción nueva.
 */
export function radioKeys<T extends HTMLElement>(group: T): T {
  const radios = () => [...group.querySelectorAll<HTMLButtonElement>('[role="radio"]')].filter((r) => !r.disabled);
  const sync = () => {
    const list = radios();
    const checked = list.find((r) => r.getAttribute('aria-checked') === 'true') ?? list[0];
    for (const r of list) r.tabIndex = r === checked ? 0 : -1;
  };
  const steps: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
  group.addEventListener('keydown', (e) => {
    const list = radios();
    const index = list.indexOf(document.activeElement as HTMLButtonElement);
    if (index < 0) return;
    const next = e.key in steps ? (index + steps[e.key]! + list.length) % list.length : e.key === 'Home' ? 0 : e.key === 'End' ? list.length - 1 : -1;
    if (next < 0) return;
    e.preventDefault();
    const target = list[next]!;
    target.focus();
    target.click();
    if (!target.isConnected) {
      const label = group.getAttribute('aria-label') ?? '';
      document.querySelector<HTMLElement>(`[role="radiogroup"][aria-label="${CSS.escape(label)}"] [role="radio"][aria-checked="true"]`)?.focus();
    }
  });
  group.addEventListener('click', () => queueMicrotask(sync));
  sync();
  return group;
}

/** Da nombre accesible a un control sin etiqueta visible (p. ej. filtros). */
export function labelled<T extends HTMLElement>(label: string, el: T): T {
  el.setAttribute('aria-label', label);
  el.title = label;
  return el;
}

export function spinner(label = t('common.loading')): HTMLElement {
  return h('div', { class: 'spinner-wrap', role: 'status' }, h('span', { class: 'spinner', attrs: { 'aria-hidden': 'true' } }), h('span', { class: 'sr-only' }, label));
}

export function emptyState(text: string, action?: HTMLElement): HTMLElement {
  return h('div', { class: 'empty' }, icon('sparkle', 28), h('p', null, text), action ?? null);
}

export function errorState(error: unknown, retry?: () => void): HTMLElement {
  return h(
    'div',
    { class: 'empty empty-error', role: 'alert' },
    icon('close', 28),
    h('p', null, describeError(error)),
    retry ? h('button', { class: 'btn btn-ghost', onClick: retry }, t('common.retry')) : null,
  );
}

export function stars(count: number, size = 16): HTMLElement {
  return h(
    'span',
    { class: 'stars', attrs: { 'aria-label': t('learn.stars', { n: count }), role: 'img' } },
    [0, 1, 2].map((i) => h('span', { class: i < count ? 'star on' : 'star' }, icon('star', size))),
  );
}

export function statTile(label: string, value: string, extra?: Child, accent = false): HTMLElement {
  return h('div', { class: `stat-tile${accent ? ' accent' : ''}` }, h('span', { class: 'stat-label' }, label), h('strong', { class: 'stat-value' }, value), extra ?? null);
}

export function pageHeader(title: string, subtitle?: string, actions?: Child): HTMLElement {
  return h('header', { class: 'page-header' }, h('div', null, h('h1', null, title), subtitle ? h('p', { class: 'lead' }, subtitle) : null), actions ? h('div', { class: 'page-actions' }, actions) : null);
}

/** Ejecuta una carga asíncrona dentro de un contenedor con estados de carga/error. */
export async function loadInto<T>(container: HTMLElement, loader: () => Promise<T>, render: (data: T) => Child): Promise<void> {
  container.replaceChildren(spinner());
  try {
    const data = await loader();
    replace(container, render(data));
  } catch (error) {
    container.replaceChildren(errorState(error, () => void loadInto(container, loader, render)));
  }
}
